// src/hooks/useMorningKickoff.ts
/**
 * Morning Kickoff 管理 Hook
 *
 * 處理早晨啟動卡片的狀態和確認邏輯
 * 從 Today.tsx 抽取，保持功能完整性
 */
import { useState, useMemo, useCallback } from 'react';
import {
  getKickoffType,
  isDailyKickoffConfirmed
} from '../components/morningKickoff/morningKickoffState';
import { formatDateToString } from '../utils/dateUtils';

export interface UseMorningKickoffOptions {
  userId: string;
  currentTime: Date;
  isDebugMode: boolean;
}

export interface UseMorningKickoffReturn {
  // 狀態
  kickoffType: 'new_week' | 'daily' | null;
  isKickoffConfirmed: boolean;
  // 方法
  handleKickoffConfirmed: () => void;
}

export function useMorningKickoff(options: UseMorningKickoffOptions): UseMorningKickoffReturn {
  const { userId, currentTime, isDebugMode } = options;

  /**
   * 追蹤 Morning Kickoff 是否已確認
   * 使用 lazy initialization 從 localStorage 讀取初始狀態
   */
  const [isKickoffConfirmed, setIsKickoffConfirmed] = useState(() => {
    if (!userId) return false;
    const dateString = formatDateToString(new Date());
    return isDailyKickoffConfirmed(userId, dateString);
  });

  /**
   * 判斷是否顯示 Morning Kickoff 以及顯示的類型
   */
  const kickoffType = useMemo(() => {
    if (!userId || isKickoffConfirmed) return null;
    return getKickoffType(userId, currentTime, isDebugMode);
  }, [userId, isDebugMode, isKickoffConfirmed, currentTime]);

  /**
   * 處理 Morning Kickoff 確認
   */
  const handleKickoffConfirmed = useCallback(() => {
    setIsKickoffConfirmed(true);
  }, []);

  return {
    kickoffType,
    isKickoffConfirmed,
    handleKickoffConfirmed,
  };
}
